import {Accordion, AccordionDetails, AccordionSummary, Card, Grid} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {SwitchCustom, SwitchSegmentedYesNo, TextBox} from '../../../Core/UI';
import {DatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import React from 'react';
import {observer} from 'mobx-react-lite';
import {HealthQuestionsProps} from './HealthQuestionsList';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';

const CancerOrTumors = observer(({counter, valueChanged, parameters, coreQuestions}: HealthQuestionsProps) => {
    return (
        <Accordion style={{width: '100%'}} TransitionProps={{unmountOnExit: true}}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={'cancerOrTumors-content'}
                id={'cancerOrTumors-header'}
            >
                <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                    <div>Cancer or tumors</div>
                    <div>{counter('cancerOrTumors')}</div>
                </div>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container>
                    <Grid key="params" container>
                        {coreQuestions?.map(({name, label}) => (
                            <Grid item xs={4} key={name}>
                                <SwitchSegmentedYesNo
                                    param={{
                                        name: name,
                                        label: label,
                                        category: 'cancerOrTumors',
                                        ...(parameters[name as keyof typeof parameters] as Parameter),
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                />
                            </Grid>
                        ))}
                        {parameters.BreastCancer.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Breast Cancer</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="breastcancerYearsRemission"
                                                label="How many years have you been in remission?"
                                                value={parameters.breastcancerYearsRemission?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.breastcancerYearsRemission, value)
                                                }
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="breastcancerTypeStage"
                                                label="Provide details to the type of cancer and stage?"
                                                value={parameters.breastcancerTypeStage?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.breastcancerTypeStage, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="breastcancerTreatment"
                                                label="What treatment was prescribed or completed for this condition? i.e. chemo, radiation, removal"
                                                value={parameters.breastcancerTreatment?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.breastcancerTreatment, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.ProstateCancer.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Prostate Cancer</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="prostatecancerRemission"
                                                label="How many years have you been in remission?"
                                                value={parameters.prostatecancerRemission?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.prostatecancerRemission, value)
                                                }
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.prostatecancerDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.prostatecancerDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="prostatecancerDiagnosisAge"
                                                label="What age were you diagnosed?"
                                                value={parameters.prostatecancerDiagnosisAge?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.prostatecancerDiagnosisAge, value)
                                                }
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'prostatecancerRecovery',
                                                    label: 'Have you had a full recovery with no complications?',
                                                    ...parameters.prostatecancerRecovery,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="prostatecancerTypeStage"
                                                label="Provide details to the type of cancer and stage?"
                                                value={parameters.prostatecancerTypeStage?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.prostatecancerTypeStage, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="prostatecancerTreatment"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.prostatecancerTreatment?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.prostatecancerTreatment, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Cancer.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Cancer</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.cancerDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.cancerDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="cancerRemissionYears"
                                                label="How many years have you been in remission? "
                                                value={parameters.cancerRemissionYears?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.cancerRemissionYears, value)
                                                }
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="cancerType"
                                                label="What type of cancer did you have? "
                                                value={parameters.cancerType?.value}
                                                onChange={(value) => valueChanged(parameters.cancerType, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="cancerStage"
                                                label="What stage of cancer?"
                                                value={parameters.cancerStage?.value}
                                                onChange={(value) => valueChanged(parameters.cancerStage, value)}
                                                type="number"
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Leukemia.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Leukemia</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.leukemiaDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.leukemiaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="leukemiaTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition? "
                                                value={parameters.leukemiaTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.leukemiaTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'leukemiaRecovery',
                                                    label: 'Have you had a full recovery with no complications? ',
                                                    ...parameters.leukemiaRecovery,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.SkinCancerMelanoma.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Skin Cancer (Melanoma)</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.melanomaDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.melanomaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="melanomaStage"
                                                label="Provide details to the type of cancer and stage?"
                                                value={parameters.melanomaStage?.value}
                                                onChange={(value) => valueChanged(parameters.melanomaStage, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="melanomaTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition? "
                                                value={parameters.melanomaTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.melanomaTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'melanomaRecovery',
                                                    label: 'Have you had a full recovery with no complications?',
                                                    ...parameters.melanomaRecovery,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.OvarianCancer.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Ovarian Cancer</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.ovariancancerDiangosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.ovariancancerDiangosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="ovariancancerRemissionYears"
                                                label="How many years have you been in remission? "
                                                value={parameters.ovariancancerRemissionYears?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.ovariancancerRemissionYears, value)
                                                }
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="ovariancancerStage"
                                                label="Provide details to the type of cancer and stage?"
                                                value={parameters.ovariancancerStage?.value}
                                                onChange={(value) => valueChanged(parameters.ovariancancerStage, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="ovariancancerTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition? "
                                                value={parameters.ovariancancerTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.ovariancancerTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'ovariancancerRecovery',
                                                    label: 'Have you had a full recovery with no complications? ',
                                                    ...parameters.ovariancancerRecovery,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Lymphoma.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Lymphoma</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.lympohomaDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.lympohomaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="lymphomaTypeStage"
                                                label="Provide details to the type of cancer and stage? "
                                                value={parameters.lymphomaTypeStage?.value}
                                                onChange={(value) => valueChanged(parameters.lymphomaTypeStage, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="lymphomaTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition? "
                                                value={parameters.lymphomaTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.lymphomaTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'lymphomaRecovery',
                                                    label: 'Have you had a full recovery with no complications?',
                                                    ...parameters.lymphomaRecovery,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.BasalCellCarcinoma.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Basal Cell Carcinoma</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.basalcellDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.basalcellDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'basalcellRecovery',
                                                    label: 'Have you had a full recovery with no complications? ',
                                                    ...parameters.basalcellRecovery,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'basalcellMelanoma',
                                                    label: 'Have you ever had a diagnosis of melanoma? ',
                                                    ...parameters.basalcellMelanoma,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
});

export default CancerOrTumors;
